import {Injectable} from "@angular/core"
import type {CmLogger, LoggerDomain, LogLevel} from "@cm/log"
import {LoggerConfig, loggerConfig, BrowserLogger} from "@cm/browser-log"

/**
 * Factory service for creating domain-specific loggers.
 * Uses @cm/browser-log under the hood with localStorage-based configuration.
 *
 * Usage:
 * - Inject LoggerFactory where you need to create loggers dynamically
 * - For static injection, use the logger tokens (CM_LOGGER_API, etc.)
 */
@Injectable({providedIn: "root"})
export class LoggerFactory {
    private readonly config: LoggerConfig = loggerConfig
    private readonly loggers = new Map<LoggerDomain, CmLogger>()

    /**
     * Get or create a logger for the specified domain.
     * The same logger instance is returned for repeated calls with the same domain.
     */
    getLogger(domain: LoggerDomain): CmLogger {
        const logger = this.loggers.get(domain)
        if (!logger) {
            const newLogger = new BrowserLogger(domain, () => this.config.getEffectiveLevel(domain))
            this.loggers.set(domain, newLogger)
            return newLogger
        }
        return logger
    }

    /**
     * Set the global log level.
     */
    setGlobalLevel(level: LogLevel): void {
        this.config.setGlobalLevel(level)
    }

    /**
     * Get the global log level.
     */
    getGlobalLevel(): LogLevel {
        return this.config.getGlobalLevel()
    }

    /**
     * Set the log level for a specific domain.
     */
    setDomainLevel(domain: LoggerDomain, level: LogLevel): void {
        this.config.setDomainLevel(domain, level)
    }

    /**
     * Clear the log level override for a domain (reverts to global).
     */
    clearDomainLevel(domain: LoggerDomain): void {
        this.config.clearDomainLevel(domain)
    }

    /**
     * Get the current configuration for debugging.
     */
    getConfig(): {global: LogLevel; domains: Partial<Record<LoggerDomain, LogLevel>>} {
        return {
            global: this.config.getGlobalLevel(),
            domains: this.config.getAllDomainLevels(),
        }
    }

    /**
     * Reset all configuration to defaults.
     */
    reset(): void {
        this.config.reset()
    }
}
